import { useNavigate, useParams } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { type TOnboardingSummary, onboardingService } from "@/api/onboarding";
import ChatView from "@/components/chat/ChatView";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import { Button } from "@/components/ui/Button";
import { useOnboardingGuard } from "@/hooks/useOnboardingGuard";
import { type TBoundedPoller, createBoundedPoller } from "@/lib/boundedPoller";
import { isSummaryStalled } from "@/lib/onboarding/sessionAdapter";
import {
  SUMMARY_OVERRUN_SECONDS,
  formatElapsedTime,
  humanizeStage,
  stageProgressPercent,
} from "@/lib/onboarding/summaryProgress";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import { extractErrorMessage } from "@/utils/errorUtils";

const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds

type TSummaryStatus = "loading" | "processing" | "failed" | "ready";

/**
 * Onboarding summary page that handles all states:
 * - Loading: Initial fetch
 * - Processing: AI is generating summary (shows spinner, polls for updates)
 * - Failed: AI generation failed (shows retry button)
 * - Ready: Summary is available (shows ChatView with Q&A history)
 */
export default function OnboardingSummaryPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const companyId = params.company_id;
  const { isLoading: isCompanyLoading, company } = useOnboardingGuard(companyId);
  const { setOnboardingSummaryMessages } = useChatStore();
  const fetchOnboardingSummary = useStore((state) => state.fetchOnboardingSummary);

  const [summaryStatus, setSummaryStatus] = useState<TSummaryStatus>("loading");
  const [summaryView, setSummaryView] = useState<"summary" | "conversation">("summary");
  const [hasTranscript, setHasTranscript] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    is_task_running?: boolean;
    elapsed_seconds?: number | null;
    current_stage?: string | null;
  } | null>(null);

  const isPollingRef = useRef(false);
  const pollerRef = useRef<TBoundedPoller | null>(null);
  const hasLoadedRef = useRef(false);
  const lastCompanyIdRef = useRef<string | null>(null);
  /** Last observed generation stage + when it last changed — feeds the stall composite. */
  const lastStageRef = useRef<string | null>(null);
  const stageChangedAtRef = useRef<number | null>(null);
  /** One free auto-retry per stalled generation; manual retries reset it. */
  const autoRetriedRef = useRef(false);
  /** Lets the poll tick reach handleRetry without a circular useCallback dep. */
  const retryRef = useRef<(() => Promise<void>) | null>(null);
  /** True once we've received a valid API response (processing/ready/failed). Used to avoid treating concurrent-fetch null as failure during polling. */
  const hasReceivedValidResponseRef = useRef(false);

  const stopPolling = useCallback(() => {
    pollerRef.current?.stop();
    pollerRef.current = null;
    isPollingRef.current = false;
  }, []);

  // Reset loaded state when company ID changes
  useEffect(() => {
    if (companyId && companyId !== lastCompanyIdRef.current) {
      // Company ID changed, reset loaded state and clear polling
      hasLoadedRef.current = false;
      hasReceivedValidResponseRef.current = false;
      lastCompanyIdRef.current = companyId;
      lastStageRef.current = null;
      stageChangedAtRef.current = null;
      autoRetriedRef.current = false;
      stopPolling();
      setSummaryStatus("loading");
      setErrorMessage(null);
    }
  }, [companyId, stopPolling]);

  /**
   * Make the transcript readable while the summary is still generating.
   *
   * The backend now returns `history` on processing responses, and it ends with the AI's
   * closing onboarding message — so a user who reloads mid-generation, or who lands here
   * before the summary lands, can still see how the conversation finished.
   *
   * Deliberately does not set `hasLoadedRef`, so the ready branch still performs its own
   * final load once the summary text exists.
   */
  const loadTranscriptWhileProcessing = useCallback(
    (summary: TOnboardingSummary) => {
      if (hasLoadedRef.current || !companyId || !summary.history?.length) return;
      setHasTranscript(true);
      setOnboardingSummaryMessages({ companyId, summary });
    },
    [companyId, setOnboardingSummaryMessages]
  );

  /**
   * Apply one fetched summary to page state.
   * Returns `false` when the caller should stop polling.
   */
  const applySummary = useCallback(
    (summary: TOnboardingSummary): boolean => {
      hasReceivedValidResponseRef.current = true;

      const stage = summary.current_stage ?? null;
      if (stage !== lastStageRef.current) {
        lastStageRef.current = stage;
        stageChangedAtRef.current = Date.now();
      }
      setSummaryData({
        is_task_running: summary.is_task_running ?? false,
        elapsed_seconds: summary.elapsed_seconds ?? null,
        current_stage: stage,
      });

      if (summary.status === "processing") {
        setErrorMessage(null);
        loadTranscriptWhileProcessing(summary);

        // Stall composite: dead-worker PROCESSING with no running task, an
        // unchanging stage past the stuck window, or a generation that has run
        // far past budget — one free auto-retry, then the honest failed CTA.
        const stalled = isSummaryStalled({
          status: "processing",
          elapsedSeconds: summary.elapsed_seconds,
          currentStage: stage,
          stageChangedAt: stageChangedAtRef.current,
        });
        if (stalled) {
          if (!autoRetriedRef.current) {
            autoRetriedRef.current = true;
            void retryRef.current?.();
            return false;
          }
          setSummaryStatus("failed");
          setErrorMessage(
            "Summary generation seems to have stalled — nothing was lost. Retry below and it should pick up."
          );
          return false;
        }
        setSummaryStatus("processing");
        return true;
      }
      if (summary.status === "failed") {
        setSummaryStatus("failed");
        setErrorMessage(summary.message ?? "AI summary generation failed. Please try again.");
        loadTranscriptWhileProcessing(summary);
        return false;
      }
      setSummaryStatus("ready");
      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setOnboardingSummaryMessages({ companyId: companyId!, summary });
      }
      return false;
    },
    [companyId, setOnboardingSummaryMessages, loadTranscriptWhileProcessing]
  );

  // Helper to start polling for summary updates — lite fetches only, so a 3s
  // cadence never pays for per-answer lookups or per-file presigns.
  const startPolling = useCallback(() => {
    stopPolling();
    isPollingRef.current = true;
    pollerRef.current = createBoundedPoller({
      intervalMs: POLL_INTERVAL_MS,
      tick: async () => {
        const summary = await fetchOnboardingSummary(companyId!, { force: true, lite: true });
        if (!summary) return true; // transient miss — keep polling
        return applySummary(summary);
      },
    });
    pollerRef.current.start();
  }, [companyId, fetchOnboardingSummary, applySummary, stopPolling]);

  // Redirect if onboarding is not completed
  useEffect(() => {
    if (!isCompanyLoading && company && !company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: companyId! },
        replace: true,
      });
    }
  }, [isCompanyLoading, company, companyId, navigate]);

  // Fetch and poll for summary status
  const fetchSummary = useCallback(async () => {
    if (!companyId) {
      return;
    }

    const summary = await fetchOnboardingSummary(companyId, { force: true });

    if (!summary) {
      // Null can mean: (a) actual API failure, or (b) store returned null due to concurrent fetch blocking.
      // During polling, overlapping requests cause the store to return null; ignore and keep polling.
      if (!hasReceivedValidResponseRef.current) {
        setSummaryStatus("failed");
        setErrorMessage("Failed to load onboarding summary");
      }
      return;
    }

    if (!applySummary(summary)) {
      stopPolling();
    }
  }, [companyId, fetchOnboardingSummary, applySummary, stopPolling]);

  // Start fetching/polling effect
  useEffect(() => {
    if (!companyId || isCompanyLoading || !company?.onboarding_completed) {
      return;
    }

    // Check if summary is already loaded in the store for this company
    const activeChatId = useChatStore.getState().activeChatId;
    const folders = useChatStore.getState().folders;
    const existingChat = folders[0]?.chats.find((chat) => chat.id === "1-1");
    const hasSummary = existingChat?.metadata?.summary !== undefined;
    // Extract companyId from metadata with type safety
    const existingCompanyId =
      existingChat?.metadata?.companyId && typeof existingChat.metadata.companyId === "string"
        ? existingChat.metadata.companyId
        : undefined;

    // If summary is already loaded for this company and active chat is set, skip loading
    if (hasSummary && existingCompanyId === companyId && activeChatId === "1-1") {
      setSummaryStatus("ready");
      hasLoadedRef.current = true;
      lastCompanyIdRef.current = companyId;
      return;
    }

    // Prevent multiple polling loops
    if (isPollingRef.current) {
      return;
    }
    isPollingRef.current = true;

    // Initial fetch (full payload — the ready path needs summary + files)
    void fetchSummary();

    // Then keep polling on the bounded lite loop
    startPolling();

    // Cleanup on unmount
    return stopPolling;
  }, [companyId, isCompanyLoading, company?.onboarding_completed, fetchSummary, startPolling, stopPolling]);

  // Handle retry. `auto` marks the single free retry a stall triggers itself —
  // it must not reset the flag, or a stalled generation would auto-retry forever.
  const handleRetry = useCallback(
    async (auto = false) => {
      if (!companyId || isRetrying) return;
      if (!auto) {
        autoRetriedRef.current = false;
      }

      setIsRetrying(true);
      setErrorMessage(null);
      setSummaryStatus("processing");

      try {
        const response = await onboardingService.retrySummaryGeneration(companyId);

        // Check if task is already running
        if (response.data?.data?.status === "error" || response.data?.message?.includes("TASK_ALREADY_RUNNING")) {
          const errorMsg =
            response.data?.data?.message ?? response.data?.message ?? "Summary generation task is already running.";
          setErrorMessage(errorMsg);
          setIsRetrying(false);
          return;
        }

        // Handle actual HTTP/API errors
        if (response.error !== null) {
          setSummaryStatus("failed");
          const errorMsg = await extractErrorMessage(response.error);
          setErrorMessage(errorMsg);
          setIsRetrying(false);
          return;
        }

        if (!response.data) {
          setSummaryStatus("failed");
          setErrorMessage("Failed to start retry. Please try again.");
          setIsRetrying(false);
          return;
        }

        // Check response data for status
        const responseData = response.data;
        if (responseData.data && typeof responseData.data === "object" && responseData.data !== null) {
          const dataObj = responseData.data as Record<string, unknown>;

          // If summary is already completed, just fetch it and render
          if (dataObj.status === "completed") {
            setIsRetrying(false);
            setErrorMessage(null);
            setSummaryStatus("loading");
            hasLoadedRef.current = false;
            await fetchSummary();
            return;
          }

          // If status is "processing", start/resume polling
          if (dataObj.status === "processing") {
            setIsRetrying(false);
            setErrorMessage(null);
            setSummaryStatus("processing");
            hasLoadedRef.current = false;
            startPolling();
            return;
          }

          // If status is "error", treat as actual failure
          if (dataObj.status === "error") {
            setSummaryStatus("failed");
            const errorMsg =
              typeof dataObj.message === "string" ? dataObj.message : "Summary generation failed. Please try again.";
            setErrorMessage(errorMsg);
            setIsRetrying(false);
            return;
          }
        }

        // If we get here and no status was found, assume processing and start polling
        setIsRetrying(false);
        setErrorMessage(null);
        setSummaryStatus("processing");
        hasLoadedRef.current = false;
        startPolling();
      } catch {
        setSummaryStatus("failed");
        setErrorMessage("An unexpected error occurred. Please try again.");
        setIsRetrying(false);
      }
    },
    [companyId, isRetrying, startPolling, fetchSummary]
  );

  // The stall path in applySummary fires the retry through this ref — the two
  // callbacks reference each other, and the ref keeps the dep graph acyclic.
  useEffect(() => {
    retryRef.current = () => handleRetry(true);
    return () => {
      retryRef.current = null;
    };
  }, [handleRetry]);

  // Handle refresh (regenerate summary when already completed)
  const handleRefresh = useCallback(async () => {
    if (!companyId || isRefreshing) return;

    setIsRefreshing(true);
    setErrorMessage(null);

    try {
      // Same pattern as handleRetry - call onboarding service method
      const refreshResponse = await onboardingService.refreshSummaryGeneration(companyId);

      // Check if task is already running or processing
      if (refreshResponse.data?.data?.status === "processing" || refreshResponse.data?.data?.status === "error") {
        if (refreshResponse.data?.data?.status === "processing") {
          // Task is processing, start polling
          setSummaryStatus("processing");
          setIsRefreshing(false);
          hasLoadedRef.current = false;
          startPolling();
          return;
        }
        // Error status - extract message safely
        const dataMessage: string =
          refreshResponse.data?.data?.message ??
          refreshResponse.data?.message ??
          "Summary generation task is already running.";
        const errorMsg = dataMessage;
        setErrorMessage(errorMsg);
        setIsRefreshing(false);
        return;
      }

      if (refreshResponse.error !== null) {
        const errorMsg = await extractErrorMessage(refreshResponse.error);
        setErrorMessage(errorMsg);
        setIsRefreshing(false);
        return;
      }

      if (!refreshResponse.data) {
        setErrorMessage("Failed to start refresh. Please try again.");
        setIsRefreshing(false);
        return;
      }

      // Refresh started successfully, switch to processing state and resume polling
      setSummaryStatus("processing");
      setIsRefreshing(false);
      hasLoadedRef.current = false;
      startPolling();
    } catch {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setIsRefreshing(false);
    }
  }, [companyId, isRefreshing, startPolling]);

  // Helper function to render retry button
  const renderRetryButton = (buttonText = "Retry Summary Generation", showForceRetry = false) => {
    // For "Force Retry", allow clicking even if backend reports a task running.
    // The backend will decide whether it can restart (e.g., cancel stale tasks).
    const isDisabled =
      isRetrying ||
      (!showForceRetry && summaryStatus === "processing") ||
      (summaryData?.is_task_running === true && !showForceRetry);
    const displayText = showForceRetry && !summaryData?.is_task_running ? "Force Retry" : buttonText;

    return (
      <Button
        onClick={() => void handleRetry()}
        disabled={isDisabled}
        className="flex items-center gap-2"
        variant={showForceRetry ? "outline" : "default"}
      >
        <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
        {isRetrying ? "Retrying..." : displayText}
      </Button>
    );
  };

  // Show loading state while checking company status
  if (isCompanyLoading || summaryStatus === "loading") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-lg font-medium text-foreground">Loading summary...</p>
          </div>
          {renderRetryButton("Retry", false)}
        </div>
      </div>
    );
  }

  // Show processing state with spinner
  if (summaryStatus === "processing") {
    const elapsedTime = formatElapsedTime(summaryData?.elapsed_seconds);
    const humanStage = humanizeStage(summaryData?.current_stage);
    const stageText = humanStage ? ` (${humanStage})` : "";
    const progressPct = stageProgressPercent(summaryData?.current_stage);
    // Only offer an interrupt once generation has clearly overrun (2 min).
    const showForceRetry = (summaryData?.elapsed_seconds ?? 0) > SUMMARY_OVERRUN_SECONDS;

    // The transcript is available on processing responses and ends with the AI's closing
    // message, so a user who reloads mid-generation can still read how it finished.
    if (showTranscript && hasTranscript) {
      return (
        <div className="relative flex h-full flex-col">
          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-2.5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>
                Still generating your summary{stageText}
                {elapsedTime && ` (${elapsedTime})`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FeedbackWidget
                context="stuck"
                companyId={companyId}
                variant="icon"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowTranscript(false)}
              >
                Back to status
              </Button>
            </div>
          </div>
          <ChatView
            view="conversation"
            onViewChange={setSummaryView}
            isTaskRunning
          />
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-lg font-medium text-foreground">
              {company?.name ? `Generating summary for ${company.name}...` : "Generating your onboarding summary..."}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              This may take a few moments...{stageText}
              {elapsedTime && ` (${elapsedTime})`}
            </p>
          </div>
          <div className="mt-4 h-1 w-64 rounded-full bg-muted">
            <div
              className="h-1 animate-pulse rounded-full bg-primary transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {hasTranscript && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowTranscript(true)}
            >
              View conversation
            </Button>
          )}
          {showForceRetry && (
            <>
              <p className="text-xs text-muted-foreground">Taking longer than expected?</p>
              {renderRetryButton("Force Retry", true)}
            </>
          )}
          <FeedbackWidget
            context="stuck"
            companyId={companyId}
            className="mt-2"
          />
        </div>
      </div>
    );
  }

  // Show failed state with retry button
  if (summaryStatus === "failed") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-6 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
            <AlertCircle className="h-8 w-8 text-amber-500" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">We couldn&apos;t generate your summary</h2>
            <p className="max-w-md text-sm text-muted-foreground">
              Something went wrong on our end. Your answers are saved — hit retry and it should be ready in a moment.
            </p>
          </div>
          {renderRetryButton("Try again", false)}
          <FeedbackWidget
            context="stuck"
            companyId={companyId}
          />
          {/* TODO: uncomment once support email is confirmed
          <p className="text-xs text-muted-foreground">
            Still not working?{" "}
            <a
              href="mailto:support@fundy.com"
              className="text-primary underline-offset-2 hover:underline"
            >
              Contact support
            </a>
          </p>
          */}
        </div>
      </div>
    );
  }

  // Summary is ready, show ChatView with refresh button
  const handleGoToDashboard = () => {
    const sections = [...(company?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (sections.length > 0 && sections[0].id) {
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/dashboard",
        params: { company_id: companyId!, section_id: sections[0].id },
      });
    } else {
      void navigate({ to: "/app" });
    }
  };

  return (
    <div className="relative flex h-full flex-col">
      <ChatView
        onRefreshSummary={() => void handleRefresh()}
        isRefreshingSummary={isRefreshing}
        isTaskRunning={summaryData?.is_task_running === true}
        view={summaryView}
        onViewChange={setSummaryView}
        summaryCtaButton={
          <>
            <FeedbackWidget
              context="summary"
              companyId={companyId}
              variant="icon"
            />
            <Button
              size="sm"
              onClick={handleGoToDashboard}
              className="flex items-center gap-1.5"
            >
              Explore Dashboard
              <ArrowRight className="h-4 w-4" />
            </Button>
          </>
        }
      />
      {errorMessage && (
        <div className="absolute right-4 top-14 z-10">
          <p className="rounded-md bg-background/80 px-3 py-1 text-sm text-destructive shadow-md backdrop-blur-sm">
            {errorMessage}
          </p>
        </div>
      )}
    </div>
  );
}
